/**
 * OcrSettingsAdmin — admin-only page at /admin/ocr-settings.
 *
 * Allows workspace admins to:
 *   - View configured OCR providers (API keys masked as '••••••••')
 *   - Add a new provider (POST /api/admin/ocr-settings)
 *   - Edit an existing provider's label, key, or default flag (PUT /:id)
 *   - Delete a provider (DELETE /:id) with confirmation
 *
 * Security note: the API never returns the real key — the masked value
 * '••••••••' is display-only. On edit, leaving the key field blank means
 * "no change"; entering a new value overwrites the stored secret.
 *
 * State is managed with TanStack Query (cache + optimistic invalidation).
 * No optimistic updates here — admin actions are infrequent, correctness
 * matters more than perceived speed.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plus, Pencil, Trash2, ShieldCheck, Eye, EyeOff, Loader2 } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useAppStore } from '../../../stores/app-store'
import { ingestApiClient } from '../ingest-api-client'
import type {
  OcrProviderSetting,
  OcrSettingCreatePayload,
  OcrSettingUpdatePayload,
} from '../ingest-api-client'

// ---------------------------------------------------------------------------
// Known provider display names (extend as backend adds support)
// ---------------------------------------------------------------------------

const PROVIDER_LABELS: Record<string, string> = {
  'mistral-ocr': 'Mistral OCR',
  'google-document-ai': 'Google Document AI',
  'custom-endpoint': 'Custom endpoint (Ollama / OpenAI-compat)',
}

const PROVIDER_OPTIONS = Object.entries(PROVIDER_LABELS).map(([value, label]) => ({
  value,
  label,
}))

/** True if this provider id requires the custom endpoint + model fields */
const isCustomEndpoint = (id: string) => id === 'custom-endpoint'

/** True if this provider id requires the Google processorPath field */
const isGoogleDocAI = (id: string) => id === 'google-document-ai'

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MASKED_SENTINEL = '••••••••'

interface ProviderFormValues {
  provider: string
  label: string
  apiKey: string
  isDefault: boolean
  endpoint: string
  model: string
  processorPath: string
}

const EMPTY_FORM: ProviderFormValues = {
  provider: PROVIDER_OPTIONS[0].value,
  label: '',
  apiKey: '',
  isDefault: false,
  endpoint: '',
  model: '',
  processorPath: '',
}

interface ProviderFormProps {
  initial?: Partial<ProviderFormValues>
  /** If true the apiKey field shows masked placeholder and blank = no-change */
  isEdit?: boolean
  onSubmit: (values: ProviderFormValues) => void
  onCancel: () => void
  submitting?: boolean
  isDark: boolean
  t: ReturnType<typeof useTranslation<'ingest'>>['t']
}

function ProviderForm({
  initial,
  isEdit = false,
  onSubmit,
  onCancel,
  submitting = false,
  isDark,
  t,
}: ProviderFormProps) {
  const [values, setValues] = useState<ProviderFormValues>({
    ...EMPTY_FORM,
    ...initial,
  })
  const [showKey, setShowKey] = useState(false)

  const set = <K extends keyof ProviderFormValues>(k: K, v: ProviderFormValues[K]) =>
    setValues((prev) => ({ ...prev, [k]: v }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(values)
  }

  const inputCls = cn(
    'w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors',
    'focus:ring-2 focus:ring-brand-500',
    isDark
      ? 'border-white/[0.08] bg-surface-3 text-neutral-200 placeholder:text-neutral-600'
      : 'border-neutral-200 bg-white text-neutral-900 placeholder:text-neutral-400',
  )

  const labelCls = cn(
    'block text-xs font-medium mb-1',
    isDark ? 'text-neutral-400' : 'text-neutral-600',
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Provider type */}
      <div>
        <label htmlFor="ocr-provider-select" className={labelCls}>
          {t('ocr-settings.provider_type')}
        </label>
        <select
          id="ocr-provider-select"
          value={values.provider}
          onChange={(e) => set('provider', e.target.value)}
          disabled={isEdit}
          className={cn(inputCls, isEdit && 'cursor-not-allowed opacity-60')}
        >
          {PROVIDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Display label */}
      <div>
        <label htmlFor="ocr-provider-label" className={labelCls}>
          {t('ocr-settings.label')}
        </label>
        <input
          id="ocr-provider-label"
          type="text"
          value={values.label}
          onChange={(e) => set('label', e.target.value)}
          placeholder={t('ocr-settings.label_placeholder')}
          required
          className={inputCls}
        />
      </div>

      {/* API key */}
      <div>
        <label htmlFor="ocr-provider-key" className={labelCls}>
          {t('ocr-settings.api_key')}
          {isEdit && (
            <span className={cn('ml-1 font-normal', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
              ({t('ocr-settings.api_key_edit_hint')})
            </span>
          )}
        </label>
        <div className="relative">
          <input
            id="ocr-provider-key"
            type={showKey ? 'text' : 'password'}
            value={values.apiKey}
            onChange={(e) => set('apiKey', e.target.value)}
            placeholder={isEdit ? MASKED_SENTINEL : t('ocr-settings.api_key_placeholder')}
            required={!isEdit}
            autoComplete="off"
            className={cn(inputCls, 'pr-9')}
          />
          <button
            type="button"
            aria-label={showKey ? t('ocr-settings.hide_key') : t('ocr-settings.show_key')}
            onClick={() => setShowKey((v) => !v)}
            className={cn(
              'absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5',
              isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600',
            )}
          >
            {showKey ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Custom-endpoint fields */}
      {isCustomEndpoint(values.provider) && (
        <>
          <div>
            <label htmlFor="ocr-provider-endpoint" className={labelCls}>Endpoint URL</label>
            <input
              id="ocr-provider-endpoint"
              type="url"
              value={values.endpoint}
              onChange={(e) => set('endpoint', e.target.value)}
              placeholder="http://ollama:11434"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="ocr-provider-model" className={labelCls}>Model</label>
            <input
              id="ocr-provider-model"
              type="text"
              value={values.model}
              onChange={(e) => set('model', e.target.value)}
              placeholder="glm-ocr:latest"
              required
              className={inputCls}
            />
          </div>
        </>
      )}

      {/* Google Document AI processor path */}
      {isGoogleDocAI(values.provider) && (
        <div>
          <label htmlFor="ocr-provider-processor" className={labelCls}>Processor path</label>
          <input
            id="ocr-provider-processor"
            type="text"
            value={values.processorPath}
            onChange={(e) => set('processorPath', e.target.value)}
            placeholder="projects/123/locations/us/processors/abc"
            required
            className={inputCls}
          />
        </div>
      )}

      {/* Default flag */}
      <div className="flex items-center gap-2">
        <input
          id="ocr-provider-default"
          type="checkbox"
          checked={values.isDefault}
          onChange={(e) => set('isDefault', e.target.checked)}
          className="h-4 w-4 rounded border-neutral-400 accent-brand-500"
        />
        <label htmlFor="ocr-provider-default" className={cn('text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
          {t('ocr-settings.set_as_default')}
        </label>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
          {isEdit ? t('ocr-settings.save') : t('ocr-settings.add')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className={cn(
            'rounded-lg px-4 py-2 text-xs font-medium transition-colors disabled:opacity-60',
            isDark
              ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200'
              : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
          )}
        >
          {t('ocr-settings.cancel')}
        </button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function OcrSettingsAdmin() {
  const { t } = useTranslation('ingest')
  const { theme } = useAppStore()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isDark = theme === 'dark'

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  // -------------------------------------------------------------------------
  // Queries & mutations
  // -------------------------------------------------------------------------

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'ocr-settings'],
    queryFn: () => ingestApiClient.listOcrSettings(),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'ocr-settings'] })

  const createMutation = useMutation({
    mutationFn: (payload: OcrSettingCreatePayload) =>
      ingestApiClient.createOcrSetting(payload),
    onSuccess: () => {
      invalidate()
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: OcrSettingUpdatePayload }) =>
      ingestApiClient.updateOcrSetting(id, payload),
    onSuccess: () => {
      invalidate()
      setEditingId(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingestApiClient.deleteOcrSetting(id),
    onSuccess: () => {
      invalidate()
      setConfirmDeleteId(null)
    },
  })

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleCreate = (values: ProviderFormValues) => {
    createMutation.mutate({
      provider: values.provider,
      label: values.label,
      // Custom endpoint often runs unauthenticated; pad the apiKey to satisfy
      // the server's z.string().min(1) when the user leaves it blank.
      apiKey: values.apiKey || (isCustomEndpoint(values.provider) ? 'none' : values.apiKey),
      isDefault: values.isDefault,
      ...(values.endpoint ? { endpoint: values.endpoint } : {}),
      ...(values.model ? { model: values.model } : {}),
      ...(values.processorPath ? { processorPath: values.processorPath } : {}),
    })
  }

  const handleUpdate = (id: string, values: ProviderFormValues) => {
    const payload: OcrSettingUpdatePayload = {
      label: values.label,
      isDefault: values.isDefault,
    }
    // Only include apiKey if user entered something (blank = no change)
    if (values.apiKey.trim()) {
      payload.apiKey = values.apiKey.trim()
    }
    updateMutation.mutate({ id, payload })
  }

  const settings: OcrProviderSetting[] = data?.settings ?? []
  const editingRow = editingId ? settings.find((s) => s.id === editingId) : null

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const rowBg = isDark
    ? 'border-white/[0.06] bg-surface-2 hover:bg-surface-3'
    : 'border-neutral-200 bg-white hover:bg-neutral-50'

  return (
    <div className={cn('min-h-screen', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Header */}
      <div
        className={cn(
          'border-b px-4 py-4 md:px-6',
          isDark ? 'border-white/[0.06]' : 'border-neutral-200',
        )}
      >
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className={cn(
              'cursor-pointer rounded-lg p-2 md:p-1.5',
              isDark
                ? 'hover:bg-surface-3 text-neutral-400'
                : 'hover:bg-neutral-100 text-neutral-500',
            )}
          >
            <ArrowLeft className="h-5 w-5 md:h-4 md:w-4" aria-hidden="true" />
          </button>
          <div>
            <h1 className={cn('text-lg font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
              {t('ocr-settings.title')}
            </h1>
            <p className={cn('text-xs mt-0.5', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
              {t('ocr-settings.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-2xl px-4 py-8 md:px-6 space-y-6">

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center gap-2 py-4" role="status" aria-label="Loading">
            <Loader2 className={cn('h-4 w-4 animate-spin', isDark ? 'text-neutral-500' : 'text-neutral-400')} aria-hidden="true" />
            <span className={cn('text-sm', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
              {t('ocr-settings.loading')}
            </span>
          </div>
        )}

        {/* Load error */}
        {error && (
          <div role="alert" className={cn('rounded-lg px-4 py-3 text-sm', isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700')}>
            {error instanceof Error ? error.message : t('errors.generic')}
          </div>
        )}

        {/* Provider list */}
        {!isLoading && settings.length > 0 && (
          <ul aria-label={t('ocr-settings.providers_list')} className="space-y-2">
            {settings.map((setting) => (
              <li
                key={setting.id}
                className={cn(
                  'rounded-xl border transition-colors',
                  rowBg,
                )}
              >
                {/* Row view */}
                {editingId !== setting.id && (
                  <div className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="min-w-0 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
                          {setting.label}
                        </span>
                        {setting.isDefault && (
                          <span
                            aria-label={t('ocr-settings.default_badge')}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                              isDark
                                ? 'bg-brand-900/50 text-brand-300'
                                : 'bg-brand-50 text-brand-700',
                            )}
                          >
                            <ShieldCheck className="h-2.5 w-2.5" aria-hidden="true" />
                            {t('ocr-settings.default')}
                          </span>
                        )}
                      </div>
                      <p className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-500')}>
                        {PROVIDER_LABELS[setting.provider] ?? setting.provider}
                      </p>
                      {/* Masked key display */}
                      <p
                        aria-label={t('ocr-settings.api_key_masked')}
                        className={cn('font-mono text-[11px] tracking-widest', isDark ? 'text-neutral-600' : 'text-neutral-400')}
                      >
                        {MASKED_SENTINEL}
                      </p>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label={`${t('ocr-settings.edit')} ${setting.label}`}
                        onClick={() => setEditingId(setting.id)}
                        className={cn(
                          'rounded-lg p-1.5 transition-colors',
                          isDark
                            ? 'text-neutral-500 hover:bg-surface-4 hover:text-neutral-300'
                            : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                        )}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        aria-label={`${t('ocr-settings.delete')} ${setting.label}`}
                        onClick={() => setConfirmDeleteId(setting.id)}
                        className={cn(
                          'rounded-lg p-1.5 transition-colors',
                          isDark
                            ? 'text-neutral-500 hover:bg-red-900/30 hover:text-red-400'
                            : 'text-neutral-400 hover:bg-red-50 hover:text-red-600',
                        )}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Inline edit form */}
                {editingId === setting.id && editingRow && (
                  <div className="px-4 py-4">
                    <p className={cn('mb-3 text-xs font-medium', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                      {t('ocr-settings.editing')}: <span className="font-semibold">{setting.label}</span>
                    </p>
                    <ProviderForm
                      isEdit
                      initial={{
                        provider: editingRow.provider,
                        label: editingRow.label,
                        apiKey: '',
                        isDefault: editingRow.isDefault,
                        // #195 — pre-fill provider-specific config so users
                        // editing one field don't accidentally wipe the rest.
                        endpoint: editingRow.endpoint ?? '',
                        model: editingRow.model ?? '',
                        processorPath: editingRow.processorPath ?? '',
                      }}
                      onSubmit={(values) => handleUpdate(setting.id, values)}
                      onCancel={() => setEditingId(null)}
                      submitting={updateMutation.isPending}
                      isDark={isDark}
                      t={t}
                    />
                    {updateMutation.error && (
                      <p role="alert" className={cn('mt-2 text-xs', isDark ? 'text-red-400' : 'text-red-600')}>
                        {updateMutation.error instanceof Error
                          ? updateMutation.error.message
                          : t('errors.generic')}
                      </p>
                    )}
                  </div>
                )}

                {/* Delete confirm inline */}
                {confirmDeleteId === setting.id && (
                  <div
                    className={cn(
                      'border-t px-4 py-3',
                      isDark ? 'border-white/[0.06]' : 'border-neutral-200',
                    )}
                  >
                    <p className={cn('mb-2 text-xs', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                      {t('ocr-settings.delete_confirm', { label: setting.label })}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => deleteMutation.mutate(setting.id)}
                        disabled={deleteMutation.isPending}
                        className="flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
                      >
                        {deleteMutation.isPending && (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        )}
                        {t('ocr-settings.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={deleteMutation.isPending}
                        className={cn(
                          'rounded-lg px-3 py-1.5 text-xs font-medium disabled:opacity-60',
                          isDark ? 'text-neutral-400 hover:bg-surface-4' : 'text-neutral-500 hover:bg-neutral-100',
                        )}
                      >
                        {t('ocr-settings.cancel')}
                      </button>
                    </div>
                    {deleteMutation.error && (
                      <p role="alert" className={cn('mt-2 text-xs', isDark ? 'text-red-400' : 'text-red-600')}>
                        {deleteMutation.error instanceof Error
                          ? deleteMutation.error.message
                          : t('errors.generic')}
                      </p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Empty state */}
        {!isLoading && settings.length === 0 && !error && (
          <div
            className={cn(
              'rounded-xl border-2 border-dashed px-6 py-10 text-center',
              isDark ? 'border-neutral-800' : 'border-neutral-200',
            )}
          >
            <ShieldCheck
              className={cn('mx-auto mb-3 h-8 w-8', isDark ? 'text-neutral-700' : 'text-neutral-300')}
              aria-hidden="true"
            />
            <p className={cn('text-sm font-medium', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
              {t('ocr-settings.empty')}
            </p>
            <p className={cn('mt-1 text-xs', isDark ? 'text-neutral-600' : 'text-neutral-400')}>
              {t('upload.no_ocr_provider')}
            </p>
          </div>
        )}

        {/* Add form */}
        {showAddForm ? (
          <div
            className={cn(
              'rounded-xl border p-5',
              isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-white',
            )}
          >
            <h2 className={cn('mb-4 text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
              {t('ocr-settings.add_title')}
            </h2>
            <ProviderForm
              onSubmit={handleCreate}
              onCancel={() => setShowAddForm(false)}
              submitting={createMutation.isPending}
              isDark={isDark}
              t={t}
            />
            {createMutation.error && (
              <p role="alert" className={cn('mt-2 text-xs', isDark ? 'text-red-400' : 'text-red-600')}>
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : t('errors.generic')}
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className={cn(
              'flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed py-3 text-sm font-medium transition-colors',
              isDark
                ? 'border-neutral-700 text-neutral-500 hover:border-neutral-500 hover:text-neutral-300'
                : 'border-neutral-200 text-neutral-400 hover:border-neutral-400 hover:text-neutral-600',
            )}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('ocr-settings.add_button')}
          </button>
        )}
      </div>
    </div>
  )
}
